import type { VideoCardType, VideoDetailType, VideoSeriesType } from "@volgo/core/contract";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router";
import { ackInput, ConfirmWarningButton, useAcks } from "../policy/ack";
import { addTag, EMPTY_QUERY, fromSearchParams, type SearchQuery, toSearchParams } from "../search/query";
import { useSession } from "../session";
import { Ambient, Cover, GalleryCard } from "../ui/GalleryCard";
import { PagedFooter } from "../ui/PagedFooter";
import { ErrorNote, Loading } from "../ui/States";
import { useLoad, usePaged } from "../ui/useLoad";
import { Player } from "./Player";
import { SeriesEditor } from "./SeriesEditor";
import {
  pending,
  posterOf,
  SeriesCard,
  time,
  useVisiblePoll,
  VideoCard,
  VideoRow,
  VideoStateNote,
  videoChanged,
  visualOf,
} from "./shared";

export function SaveVideo({
  video,
  saved = false,
  onChange,
}: {
  video: VideoCardType;
  saved?: boolean;
  onChange?: () => void;
}) {
  const { client } = useSession();
  const [isSaved, setSaved] = useState(saved);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>();
  if (video.policy.level === "block") return null;
  return (
    <>
      <button
        type="button"
        className="btn"
        disabled={busy}
        aria-pressed={isSaved}
        onClick={async () => {
          setBusy(true);
          setError(undefined);
          try {
            await client.call(isSaved ? "videos.unsave" : "videos.save", { params: { id: video.id } });
            setSaved(!isSaved);
            onChange?.();
          } catch (e) {
            setError(e);
          } finally {
            setBusy(false);
          }
        }}
      >
        {isSaved ? "저장 해제" : "저장"}
      </button>
      {error !== undefined && <ErrorNote error={error} />}
    </>
  );
}
function Share() {
  const [message, setMessage] = useState("");
  return (
    <>
      <button
        type="button"
        className="btn"
        onClick={() =>
          navigator.clipboard.writeText(window.location.href).then(
            () => setMessage("주소를 복사했습니다"),
            () => setMessage("주소를 복사하지 못했습니다. 주소 표시줄에서 복사하세요."),
          )
        }
      >
        공유
      </button>
      <span role="status">{message}</span>
    </>
  );
}
export function VideoTags({ tags }: { tags: string[] }) {
  const navigate = useNavigate();
  const [draft, setDraft] = useState<SearchQuery>(EMPTY_QUERY);
  return (
    <section className="video-tags">
      <h3>태그</h3>
      {tags.map((tag) => (
        <span className="video-tag" key={tag}>
          {tag}
          <button
            type="button"
            aria-label={`${tag} 포함`}
            aria-pressed={draft.tags.includes(tag)}
            onClick={() => setDraft(addTag(draft, tag, "include"))}
          >
            +
          </button>
          <button
            type="button"
            aria-label={`${tag} 제외`}
            aria-pressed={draft.xtags.includes(tag)}
            onClick={() => setDraft(addTag(draft, tag, "exclude"))}
          >
            −
          </button>
        </span>
      ))}
      {(draft.tags.length > 0 || draft.xtags.length > 0) && (
        <button
          type="button"
          className="btn"
          onClick={() => navigate(`/videos/list?${toSearchParams(draft)}`)}
        >
          태그 적용
        </button>
      )}
    </section>
  );
}
export function Originals({ links }: { links: VideoDetailType["links"] }) {
  const location = useLocation();
  return links.length ? (
    <section className="video-originals">
      <h3>원작 동인지</h3>
      {links.map((g) => (
        <GalleryCard key={g.gid} card={g} to={`/g/${g.gid}`} state={{ inApp: true, background: location }} />
      ))}
    </section>
  ) : null;
}

export function VideoHomePage() {
  const { client } = useSession();
  const home = useLoad(() => client.call("videos.home"), "home");
  const [chosen, setChosen] = useState(0);
  if (!home.data)
    return home.status === "error" ? <ErrorNote error={home.error} onRetry={home.reload} /> : <Loading />;
  const h = home.data;
  const candidates = [
    ...new Map([...h.continueWatching, ...h.popular, ...h.newest].map((v) => [v.id, v])).values(),
  ].slice(0, 5);
  const v = candidates[chosen] ?? candidates[0];
  const detail = v && [...h.featured, ...h.continueWatching].find((x) => x.id === v.id);
  const poster = v ? posterOf(v) : null;
  return (
    <div className="video-home">
      {v ? (
        <section className="video-hero" data-policy={v.policy.level}>
          {poster && v.policy.level === "none" && <Ambient src={poster} />}
          <div className="video-hero-copy">
            <p>
              {detail?.progress && !detail.progress.completed
                ? `이어 보기 · ${time(detail.progress.positionMs)} / ${time(v.durationMs)}`
                : h.popular[0]?.id === v.id
                  ? "오늘 많이 본 1위"
                  : "새 영상"}
            </p>
            <h1>{v.title}</h1>
            <p>
              {v.uploader.name} · {v.height ? `${v.height}p · ` : ""}
              {time(v.durationMs)}
            </p>
            <div className="video-hero-tags">
              {detail?.tags.slice(0, 5).map((t) => (
                <span key={t}>{t}</span>
              ))}
            </div>
            <div className="video-actions">
              <Link className="btn btn-pri" to={`/videos/${v.id}`}>
                {detail?.progress && !detail.progress.completed ? "이어 보기" : "재생"}
              </Link>
              <SaveVideo key={v.id} video={v} saved={detail?.saved} />
              <Link className="btn" to={v.series ? `/videos/s/${v.series.id}` : `/videos/${v.id}`}>
                작품 정보
              </Link>
            </div>
            <fieldset className="video-dots" aria-label="첫 화면 영상">
              {candidates.map((x, i) => (
                <button
                  key={x.id}
                  type="button"
                  aria-label={`${i + 1}번째 영상`}
                  aria-pressed={i === chosen}
                  onClick={() => setChosen(i)}
                />
              ))}
            </fieldset>
          </div>
          <div className={`video-hero-poster ${poster ? "" : "hatch"}`}>
            {poster && <Cover src={poster} veiled={v.policy.level === "warn"} />}
            {v.policy.level === "block" && <p>차단된 태그가 포함되어 있습니다</p>}
          </div>
        </section>
      ) : (
        <div className="page">
          <h1>영상</h1>
          <p>아직 공개된 영상이 없습니다.</p>
          <Link to="/videos/upload">첫 영상 올리기</Link>
        </div>
      )}
      <div className="video-home-rows">
        {h.continueWatching.length > 0 && (
          <VideoRow title="이어 보기" href="/videos/list">
            {h.continueWatching.slice(0, 12).map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </VideoRow>
        )}
        {h.popular.length > 0 && (
          <VideoRow title="오늘 많이 본 영상" href="/videos/list?sort=popular">
            {h.popular.slice(0, 10).map((v, i) => (
              <VideoCard key={v.id} video={v} rank={i + 1} />
            ))}
          </VideoRow>
        )}
        {h.linked.length > 0 && (
          <VideoRow title="원작 동인지가 있는 영상" href="/videos/list">
            {h.linked.slice(0, 12).map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </VideoRow>
        )}
        {h.series.length > 0 && (
          <VideoRow title="영상 작품" href="/videos/series">
            {h.series.slice(0, 12).map((s) => (
              <SeriesCard key={s.id} series={s} />
            ))}
          </VideoRow>
        )}
        {h.newest.length > 0 && (
          <VideoRow title="새 영상" href="/videos/list?sort=new">
            {h.newest.slice(0, 12).map((v) => (
              <VideoCard key={v.id} video={v} />
            ))}
          </VideoRow>
        )}
      </div>
    </div>
  );
}
export function VideoListPage({ saved = false }: { saved?: boolean }) {
  const { client } = useSession();
  const [params, setParams] = useSearchParams();
  const query = fromSearchParams(params);
  const raw = params.get("sort");
  const sort = raw === "popular" || raw === "views" ? raw : "new";
  const list = usePaged(
    (cursor) =>
      saved
        ? client.call("videos.saved", { query: { cursor, limit: 80 } })
        : client.call("videos.list", {
            query: {
              cursor,
              limit: 80,
              sort,
              q: query.text || undefined,
              tag: [...query.tags],
              xtag: [...query.xtags],
            },
          }),
    `${saved}:${params}`,
  );
  return (
    <div className="page">
      <div className="page-head">
        <h1>{saved ? "저장한 영상" : "영상"}</h1>
        {!saved && (
          <select
            aria-label="영상 정렬"
            value={sort}
            onChange={(e) => {
              const next = new URLSearchParams(params);
              next.set("sort", e.target.value);
              setParams(next);
            }}
          >
            <option value="new">새 영상</option>
            <option value="popular">인기 (24시간)</option>
            <option value="views">조회수</option>
          </select>
        )}
        <Link to="/videos/series">영상 작품</Link>
      </div>
      <div className="video-grid">
        {list.items.map((v) => (
          <VideoCard key={v.id} video={v}>
            {saved && (
              <SaveVideo
                video={v}
                saved
                onChange={() => list.setItems((items) => items.filter((x) => x.id !== v.id))}
              />
            )}
          </VideoCard>
        ))}
      </div>
      <PagedFooter
        list={list}
        emptyTitle={saved ? "저장한 영상이 없습니다" : "검색 결과가 없습니다"}
        moreLabel="80개 더 보기"
      />
    </div>
  );
}
export function SeriesListPage() {
  const { client } = useSession();
  const list = usePaged<VideoSeriesType>(
    (cursor) => client.call("videos.seriesList", { query: { cursor, limit: 80 } }),
    "series",
  );
  return (
    <div className="page">
      <h1>영상 작품</h1>
      <div className="video-grid video-series-grid">
        {list.items.map((s) => (
          <SeriesCard key={s.id} series={s} />
        ))}
      </div>
      <PagedFooter list={list} emptyTitle="아직 작품이 없습니다" moreLabel="80개 더 보기" />
    </div>
  );
}
export function SeriesPage() {
  const { id = "" } = useParams();
  return <SeriesBody key={id} id={id} />;
}
function SeriesBody({ id }: { id: string }) {
  const { client } = useSession();
  const loaded = useLoad(() => client.call("videos.seriesGet", { params: { id } }), id);
  const [tab, setTab] = useState("episodes");
  const [editing, setEditing] = useState(false);
  useVisiblePoll(!!loaded.data?.episodes.some((v) => pending(v.state)), async () =>
    loaded.set(await client.call("videos.seriesGet", { params: { id } })),
  );
  if (!loaded.data)
    return loaded.status === "error" ? (
      <ErrorNote error={loaded.error} onRetry={loaded.reload} />
    ) : (
      <Loading />
    );
  const s = loaded.data;
  const image = visualOf(s);
  const first =
    s.episodes.find((v) => v.progress && !v.progress.completed) ??
    s.episodes.find((v) => !v.progress?.completed) ??
    s.episodes[0];
  return (
    <div className="video-series-page" data-policy={s.policy.level}>
      {image && s.policy.level === "none" && <Ambient src={image} />}
      <div className="video-series-head">
        <div className={`series-key ${image ? "" : "hatch"}`}>
          {image && <Cover src={image} veiled={s.policy.level === "warn"} />}
        </div>
        <div>
          <p>영상 · {s.episodeCount}편</p>
          <h1>{s.title}</h1>
          {s.altTitle && <p>{s.altTitle}</p>}
          <p>
            {s.owner.name} · 영상 {s.episodeCount}편
          </p>
          <p>
            {Math.round(s.totalDurationMs / 60000)}분 · {s.resolutions.map((r) => `${r}p`).join(", ")} · 조회{" "}
            {s.viewCount} · {new Date(s.createdAt).toLocaleDateString("ko-KR")}
          </p>
          <p className="video-description">{s.description}</p>
          <div className="video-actions">
            {first && s.policy.level !== "block" && (
              <>
                <Link className="btn btn-pri" to={`/videos/${first.id}`}>
                  {first.progress && !first.progress.completed
                    ? `${first.series?.episodeNo ?? 1}화 이어 보기`
                    : "1화부터 재생"}
                </Link>
                <SaveVideo video={first} saved={first.saved} />
              </>
            )}
            <Share />
            {s.isOwner && (
              <button type="button" className="btn" onClick={() => setEditing(true)}>
                작품 편집
              </button>
            )}
          </div>
        </div>
      </div>
      {editing && <SeriesEditor series={s} close={() => setEditing(false)} saved={loaded.reload} />}
      <div className="video-series-columns">
        <section>
          <nav className="tabs">
            <button type="button" aria-pressed={tab === "episodes"} onClick={() => setTab("episodes")}>
              회차
            </button>
            <button type="button" aria-pressed={tab === "related"} onClick={() => setTab("related")}>
              관련 영상
            </button>
          </nav>
          {tab === "episodes" ? (
            s.episodes.map((v) => (
              <div
                className="video-episode"
                key={v.id}
                data-current={v.progress && !v.progress.completed ? "true" : undefined}
              >
                <b>{v.series?.episodeNo ?? "—"}</b>
                <VideoCard video={v} />
                <div>
                  <h3>
                    {v.series?.episodeNo ?? 1}화 · {v.title}
                  </h3>
                  <p>{v.description}</p>
                  {v.state && v.state.status !== "published" && <VideoStateNote state={v.state} />}
                </div>
              </div>
            ))
          ) : (
            <RelatedSeries seriesId={id} tags={s.tags} />
          )}
        </section>
        <aside>
          <VideoTags tags={s.tags} />
          <Originals links={s.links} />
          <p>태그와 원작 동인지의 정책이 영상에도 적용됩니다.</p>
        </aside>
      </div>
    </div>
  );
}
function RelatedSeries({ seriesId, tags }: { seriesId: string; tags: string[] }) {
  const { client } = useSession();
  const data = useLoad(
    () => client.call("videos.list", { query: { tag: tags.slice(0, 1), limit: 12 } }),
    seriesId,
  );
  return (
    <div className="video-grid">
      {data.data?.items
        .filter((v) => v.series?.id !== seriesId)
        .map((v) => (
          <VideoCard key={v.id} video={v} />
        ))}
    </div>
  );
}
export function WatchPage() {
  const { id = "" } = useParams();
  return <WatchBody key={id} id={id} />;
}
function WatchBody({ id }: { id: string }) {
  const { client } = useSession();
  const acks = useAcks();
  const confirmed = acks.isConfirmed(id);
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState<number | null>(null);
  const loaded = useLoad(
    () => client.call("videos.get", { params: { id }, query: ackInput(acks, id) }),
    `${id}:${confirmed}`,
  );
  useVisiblePoll(pending(loaded.data?.state), async () => {
    const state = await client.call("videos.status", { params: { id } });
    if (state.status === "published" || state.status === "failed" || state.status === "removed") {
      loaded.set(await client.call("videos.get", { params: { id }, query: ackInput(acks, id) }));
      videoChanged();
    } else if (loaded.data) loaded.set({ ...loaded.data, state });
  });
  const v = loaded.data;
  const episodes = v?.episodes ?? [];
  const at = episodes.findIndex((x) => x.id === id);
  const next = at >= 0 ? episodes[at + 1] : undefined;
  useEffect(() => {
    if (countdown === null || !next || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    if (countdown === 0) {
      navigate(`/videos/${next.id}`);
      return;
    }
    const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown, next, navigate]);
  if (!v)
    return loaded.status === "error" ? (
      <ErrorNote error={loaded.error} onRetry={loaded.reload} />
    ) : (
      <Loading />
    );
  const blocked = v.policy.level === "block";
  const warned = v.policy.level === "warn" && !confirmed;
  const poster = posterOf(v);
  return (
    <div className="video-watch" data-policy={v.policy.level}>
      <section>
        <div className="video-player-wrap">
          {poster && !warned && !blocked && <Ambient src={poster} />}
          {blocked ? (
            <div className="video-playback-error hatch">
              차단된 태그가 포함되어 있습니다. 이 영상은 재생할 수 없습니다.
            </div>
          ) : warned ? (
            <div className="video-playback-error">
              <p>경고 태그가 있습니다: {v.policy.level === "warn" && v.policy.matchedTags.join(", ")}</p>
              <ConfirmWarningButton gid={id} />
            </div>
          ) : v.state && v.state.status !== "published" ? (
            <div className="video-processing">
              <VideoStateNote state={v.state} />
              {v.state.status === "failed" && (
                <Link to="/videos/upload" state={{ retry: v }}>
                  다시 올리기
                </Link>
              )}
            </div>
          ) : "playlist" in v && v.playlist ? (
            <Player video={v} onEnded={() => next && setCountdown(5)} />
          ) : (
            <p>재생할 수 있는 영상이 없습니다.</p>
          )}
        </div>
        {countdown !== null && next && (
          <div role="status" className="video-next-prompt">
            <Link to={`/videos/${next.id}`}>
              다음 회차 재생
              {!window.matchMedia("(prefers-reduced-motion: reduce)").matches && ` · ${countdown}초`}
            </Link>
            <button type="button" onClick={() => setCountdown(null)}>
              취소
            </button>
          </div>
        )}
        <h1>
          {v.series && (
            <Link className="video-series-label" to={`/videos/s/${v.series.id}`}>
              {v.series.title} · {v.series.episodeNo}화
            </Link>
          )}
          {v.title}
        </h1>
        <div className="video-actions">
          <span>{v.uploader.name}</span>
          <SaveVideo video={v} saved={v.saved} />
          <Share />
        </div>
        <div className="video-description">
          <b>
            조회 {v.viewCount.toLocaleString()} ·{" "}
            {v.publishedAt ? new Date(v.publishedAt).toLocaleDateString("ko-KR") : "처리 중"}
          </b>
          <p>{v.description}</p>
          <VideoTags tags={v.tags} />
        </div>
      </section>
      {!blocked && (
        <aside>
          {episodes.length > 0 && (
            <section className="video-episodes-side">
              <h3>이 작품의 회차</h3>
              {episodes.map((e) => (
                <VideoCard key={e.id} video={e} />
              ))}
            </section>
          )}
          <Originals links={v.links} />
          {(v.nextVideos?.length ?? 0) > 0 && (
            <section>
              <h3>다음 영상</h3>
              {v.nextVideos
                ?.filter((n) => n.id !== id)
                .slice(0, 8)
                .map((n) => (
                  <VideoCard key={n.id} video={n} />
                ))}
            </section>
          )}
        </aside>
      )}
    </div>
  );
}
